import { useVirtualizer } from '@tanstack/react-virtual';
import { ArrowUp, ChevronDown, ChevronRight } from 'lucide-react';
import { type ReactNode, type RefObject, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { type ChronologyEventLight, isBackgroundEvent } from '@/hooks/useChronology';
import { cn } from '@/lib/utils';
import type { RegionalDateLocale } from '@/utils/date-utils';
import { formatTemporalCompact } from '@/utils/temporal';
import { SearchHighlightedText } from './search-highlight';
import { getCategoryStyle, getEventCategoryName, getTagStyle, type GroupStyle } from './styles';

const FILTER_MOTION_MS = 200;
/** A second change inside this window settles instead of starting another collapse. */
const FILTER_MOTION_BURST_MS = 400;
/** Past this, collapsing every added and removed row is enough work to stall typing. */
const MAX_ANIMATED_ROW_CHANGES = 24;
const TIMELINE_VIRTUALIZATION_THRESHOLD = 200;

interface MotionRow {
  event: ChronologyEventLight;
  leaving: boolean;
  entering: boolean;
  /** Collapsed and faded. Leaving rows move here; entering rows start here. */
  hidden: boolean;
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

/** Keeps removed events in place for one beat so the rows below can slide up as they fade. */
function settledRows(events: readonly ChronologyEventLight[]): MotionRow[] {
  return events.map(event => ({ event, leaving: false, entering: false, hidden: false }));
}

/** Survivors of a filter keep their relative order. A date edit does not. */
function sameSurvivingOrder(previous: readonly { id: string }[], next: readonly { id: string }[]): boolean {
  const nextIndex = new Map(next.map((event, index) => [event.id, index]));
  let last = -1;
  for (const event of previous) {
    const index = nextIndex.get(event.id);
    if (index === undefined) continue;
    if (index < last) return false;
    last = index;
  }
  return true;
}

export function listMotionRows(previous: readonly ChronologyEventLight[], next: readonly ChronologyEventLight[]): MotionRow[] {
  if (!sameSurvivingOrder(previous, next)) return settledRows(next);
  return withLeaving(previous, next);
}

/**
 * Rows for a filter animation, or null when the list should jump to the result.
 * A virtualized list has to mount every row to collapse it, and a big churn
 * does the same, so those updates settle in place.
 */
export function rowsForFilterMotion(
  previous: readonly ChronologyEventLight[],
  next: readonly ChronologyEventLight[],
): MotionRow[] | null {
  if (
    previous.length > TIMELINE_VIRTUALIZATION_THRESHOLD
    || next.length > TIMELINE_VIRTUALIZATION_THRESHOLD
  ) return null;
  const rows = listMotionRows(previous, next);
  let changes = 0;
  for (const row of rows) {
    if (!row.leaving && !row.entering) continue;
    changes += 1;
    if (changes > MAX_ANIMATED_ROW_CHANGES) return null;
  }
  return rows;
}

function withLeaving(previous: readonly ChronologyEventLight[], next: readonly ChronologyEventLight[]): MotionRow[] {
  const nextIds = new Set(next.map(event => event.id));
  const previousIds = new Set(previous.map(event => event.id));
  const rows: MotionRow[] = [];
  let nextIndex = 0;
  const entering = (event: ChronologyEventLight): MotionRow => ({ event, leaving: false, entering: true, hidden: true });
  const staying = (event: ChronologyEventLight): MotionRow => ({ event, leaving: false, entering: false, hidden: false });

  for (const event of previous) {
    if (!nextIds.has(event.id)) {
      rows.push({ event, leaving: true, entering: false, hidden: false });
      continue;
    }
    while (nextIndex < next.length && next[nextIndex].id !== event.id) {
      if (!previousIds.has(next[nextIndex].id)) rows.push(entering(next[nextIndex]));
      nextIndex += 1;
    }
    rows.push(staying(next[nextIndex] ?? event));
    nextIndex += 1;
  }
  while (nextIndex < next.length) {
    const event = next[nextIndex];
    rows.push(previousIds.has(event.id) ? staying(event) : entering(event));
    nextIndex += 1;
  }
  return rows;
}

function useMotionRows(events: ChronologyEventLight[], scope: string): MotionRow[] {
  const previousRef = useRef(events);
  const scopeRef = useRef(scope);
  const motionStartedAtRef = useRef(0);
  const [rows, setRows] = useState<MotionRow[]>(() => settledRows(events));

  const scopeChanged = scopeRef.current !== scope;
  if (scopeChanged || previousRef.current !== events) {
    const previous = previousRef.current;
    const inFlight = rows.some(row => row.leaving || row.entering);
    // Quick typing otherwise starts a new collapse as soon as the last one ends.
    const burst = performance.now() - motionStartedAtRef.current < FILTER_MOTION_BURST_MS;
    scopeRef.current = scope;
    previousRef.current = events;
    const motion = scopeChanged || prefersReducedMotion() || inFlight || burst
      ? null
      : rowsForFilterMotion(previous, events);
    if (motion?.some(row => row.leaving || row.entering)) motionStartedAtRef.current = performance.now();
    setRows(motion ?? settledRows(events));
  }

  const leaving = rows.some(row => row.leaving);
  const settled = !rows.some(row => (row.leaving && !row.hidden) || (row.entering && row.hidden));
  useLayoutEffect(() => {
    if (!leaving && !rows.some(row => row.entering)) return undefined;
    if (!settled) {
      const frame = requestAnimationFrame(() => {
        setRows(current => current.map(row => {
          if (row.leaving) return { ...row, hidden: true };
          if (row.entering) return { ...row, hidden: false };
          return row;
        }));
      });
      return () => cancelAnimationFrame(frame);
    }
    const timeout = window.setTimeout(() => {
      setRows(current => current
        .filter(row => !row.leaving)
        .map(row => ({ ...row, entering: false, hidden: false })));
    }, FILTER_MOTION_MS);
    return () => window.clearTimeout(timeout);
  }, [leaving, settled, rows]);

  return rows;
}

function MotionRowFrame({ row, children }: { row: MotionRow; children: ReactNode }) {
  const moving = row.leaving || row.entering;
  return (
    <div
      className={cn(
        // The row has to stretch with the track. items-start leaves it at full
        // height, so collapsing text paints over the rows below it.
        'grid transition-[grid-template-rows,opacity] duration-200 ease-out motion-reduce:transition-none',
        moving && 'overflow-hidden',
        row.hidden ? 'pointer-events-none grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100',
      )}
    >
      <div className={cn('min-h-0', moving && 'overflow-hidden')}>
        {children}
      </div>
    </div>
  );
}

const TIMELINE_EVENT_ROW_HEIGHT_PX = 88;
const TIMELINE_YEAR_MARKER_HEIGHT_PX = 36;

const DATE_COLUMN_CLASSNAME = 'w-[56px] shrink-0 tabular-nums';
const YEAR_LABEL_CLASSNAME = 'whitespace-nowrap text-xs font-medium leading-5 tracking-[0.02em] text-gray-800';
/** Year rows and the sticky year jump share this geometry so the jump sits exactly over the first marker. */
const YEAR_ROW_CLASSNAME = 'h-[36px] pt-2.5';
/**
 * Centred in the spine column (56px date column + half of the 23px spine column),
 * starting at the first node and fading out after the last.
 */
const SPINE_LINE_CLASSNAME =
  'pointer-events-none absolute bottom-0 left-[67px] w-px bg-slate-900/[0.09] [mask-image:linear-gradient(to_bottom,black_calc(100%-56px),transparent)]';

interface EventNodeProps {
  ev: ChronologyEventLight;
  isSelected: boolean;
  colorByCategory?: ReadonlyMap<string, string | null>;
  activeCategories?: ReadonlySet<string>;
  excludedTypes?: ReadonlySet<string>;
}

function EventNode({ ev, isSelected, colorByCategory, activeCategories, excludedTypes }: EventNodeProps) {
  const style = getEventAccentStyle(ev, colorByCategory, activeCategories, excludedTypes);
  return (
    <div className="relative h-9 w-[23px] shrink-0" aria-hidden="true">
      <span
        className={cn(
          'absolute left-1/2 top-[18px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-(--chron-surface,white)',
          'ring-[3px] ring-(--chron-surface,white) transition-[width,height,transform,box-shadow] duration-150',
          isSelected
            ? 'h-[9px] w-[9px] shadow-[0_0_0_4.5px_rgba(15,23,42,0.22)]'
            : 'h-[7px] w-[7px] group-hover/row:scale-[1.3]',
        )}
      >
        <span className={cn('absolute inset-0 rounded-full', isSelected ? style.activeDot : style.dot)} />
      </span>
    </div>
  );
}

function chronologyYear(date: Date | null | undefined): number | null {
  return date ? date.getUTCFullYear() : null;
}

function chronologyYearKey(date: Date | null | undefined): string | null {
  const year = chronologyYear(date);
  return year === null ? null : year.toString();
}

function timelineRowHasYearMarker(events: ChronologyEventLight[], index: number): boolean {
  const currentYear = chronologyYearKey(events[index]?.temporal_lower);
  const previousYear = index > 0 ? chronologyYearKey(events[index - 1]?.temporal_lower) : null;
  return Boolean(currentYear && currentYear !== previousYear);
}

function timelineRowSize(events: ChronologyEventLight[], index: number): number {
  return TIMELINE_EVENT_ROW_HEIGHT_PX + (timelineRowHasYearMarker(events, index) ? TIMELINE_YEAR_MARKER_HEIGHT_PX : 0);
}

/** A year label adds a fixed band, so a row with and without one is a different size. */
function timelineVirtualRowKey(id: string, hasYearMarker: boolean): string {
  return hasYearMarker ? `${id}:year` : id;
}

function eventRow(container: HTMLElement, eventId: string): HTMLElement | null {
  return container.querySelector<HTMLElement>(`[data-event-id="${eventId}"]`);
}

function eventRowIsRendered(container: HTMLDivElement | null, eventId: string): boolean {
  if (!container) return false;
  return !!eventRow(container, eventId);
}

/** First row still intersecting the scrollport, in viewport coordinates. */
function visibleRowAnchor(container: HTMLElement): { id: string; top: number } | null {
  const topEdge = container.getBoundingClientRect().top;
  for (const row of container.querySelectorAll<HTMLElement>('[data-event-id]')) {
    const id = row.dataset.eventId;
    if (!id) continue;
    const top = row.getBoundingClientRect().top;
    if (top + row.offsetHeight > topEdge + 1) return { id, top };
  }
  return null;
}

function eventShowsDate(events: readonly ChronologyEventLight[], index: number): boolean {
  const current = events[index];
  if (!current) return false;
  const previous = index > 0 ? events[index - 1].temporal_expression : null;
  return !previous || current.temporal_expression !== previous;
}

/**
 * Year labels stay on the first row of that year. A year that survives the
 * filter keeps its label outside the collapsing row, so removing the rows
 * that left does not drop the label into a new place.
 */
function motionYearMarkers(rows: readonly MotionRow[]): Map<string, 'outside' | 'inside'> {
  const surviving = new Set<string>();
  for (const row of rows) {
    if (row.leaving) continue;
    const year = chronologyYearKey(row.event.temporal_lower);
    if (year) surviving.add(year);
  }
  const markers = new Map<string, 'outside' | 'inside'>();
  const seen = new Set<string>();
  for (const row of rows) {
    const year = chronologyYearKey(row.event.temporal_lower);
    if (!year || seen.has(year)) continue;
    seen.add(year);
    markers.set(row.event.id, surviving.has(year) ? 'outside' : 'inside');
  }
  return markers;
}

function motionDateVisibility(rows: readonly MotionRow[], destination: readonly ChronologyEventLight[]): Map<string, boolean> {
  const destinationIndex = new Map(destination.map((event, index) => [event.id, index]));
  const visibility = new Map<string, boolean>();
  let previousExpression: string | null | undefined;
  for (const row of rows) {
    const index = destinationIndex.get(row.event.id);
    if (!row.leaving && index !== undefined) {
      visibility.set(row.event.id, eventShowsDate(destination, index));
      previousExpression = row.event.temporal_expression;
      continue;
    }
    visibility.set(
      row.event.id,
      !previousExpression || row.event.temporal_expression !== previousExpression,
    );
    previousExpression = row.event.temporal_expression;
  }
  return visibility;
}

/**
 * The detail panel narrows the list after a click, and rows above the selection
 * reflow. Hold the clicked row at its viewport position until that layout settles.
 */
function usePinnedRow(scrollContainerRef: RefObject<HTMLDivElement | null>) {
  const anchorRef = useRef<{ id: string; top: number } | null>(null);
  const correctingRef = useRef(false);
  const releaseTimerRef = useRef<number | null>(null);

  const clearRelease = useCallback(() => {
    if (releaseTimerRef.current === null) return;
    window.clearTimeout(releaseTimerRef.current);
    releaseTimerRef.current = null;
  }, []);

  const scheduleRelease = useCallback(() => {
    clearRelease();
    releaseTimerRef.current = window.setTimeout(() => {
      anchorRef.current = null;
      releaseTimerRef.current = null;
    }, 200);
  }, [clearRelease]);

  const restore = useCallback(() => {
    const anchor = anchorRef.current;
    const container = scrollContainerRef.current;
    if (!anchor || !container) return;
    const row = eventRow(container, anchor.id);
    if (!row) return;
    const delta = row.getBoundingClientRect().top - anchor.top;
    if (Math.abs(delta) < 1) return;
    correctingRef.current = true;
    container.scrollTop += delta;
    correctingRef.current = false;
    scheduleRelease();
  }, [scheduleRelease, scrollContainerRef]);

  const pin = useCallback((id: string) => {
    const container = scrollContainerRef.current;
    const row = container ? eventRow(container, id) : null;
    if (!row) {
      anchorRef.current = null;
      return;
    }
    anchorRef.current = { id, top: row.getBoundingClientRect().top };
    scheduleRelease();
  }, [scheduleRelease, scrollContainerRef]);

  useLayoutEffect(() => {
    restore();
  });

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(() => restore());
    observer.observe(container);
    if (container.firstElementChild) observer.observe(container.firstElementChild);
    const onScroll = () => {
      if (correctingRef.current) return;
      clearRelease();
      anchorRef.current = null;
    };
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      observer.disconnect();
      container.removeEventListener('scroll', onScroll);
      clearRelease();
    };
  }, [clearRelease, restore, scrollContainerRef]);

  const clear = useCallback(() => {
    anchorRef.current = null;
    clearRelease();
  }, [clearRelease]);

  return { pin, clear };
}

interface TimelineYearNavItem {
  year: string;
  index: number;
}

function timelineYearNavItems(events: ChronologyEventLight[]): TimelineYearNavItem[] {
  const items: TimelineYearNavItem[] = [];
  const seen = new Set<string>();

  events.forEach((ev, index) => {
    const year = chronologyYearKey(ev.temporal_lower);
    if (!year || seen.has(year)) return;
    seen.add(year);
    items.push({ year, index });
  });

  return items;
}

interface TimelineYearJumpProps {
  years: TimelineYearNavItem[];
  activeYear: string | null;
  onSelectYear: (item: TimelineYearNavItem) => void;
}

function TimelineYearJump({ years, activeYear, onSelectYear }: TimelineYearJumpProps) {
  const [open, setOpen] = useState(false);
  if (years.length < 2) return null;
  const label = activeYear ?? years[0]?.year;

  return (
    <div
      className={cn('sticky top-0 z-20 -mb-[36px] flex w-[56px] items-start bg-(--chron-surface,white)', YEAR_ROW_CLASSNAME)}
      onBlur={event => {
        const nextTarget = event.relatedTarget;
        if (!(nextTarget instanceof Node) || !event.currentTarget.contains(nextTarget)) {
          setOpen(false);
        }
      }}
    >
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen(value => !value)}
          className={cn('group/jump relative tabular-nums transition-colors hover:text-gray-950', YEAR_LABEL_CLASSNAME)}
          aria-expanded={open}
          aria-label="Jump to year"
          title="Jump to year"
        >
          {label}
          <ChevronDown
            className={cn(
              'absolute left-full top-1/2 ml-1 h-3 w-3 -translate-y-1/2 rounded-sm bg-(--chron-surface,white) text-gray-400 transition-[opacity,transform] duration-150',
              open ? 'rotate-180 opacity-100' : 'opacity-0 group-hover/jump:opacity-100 group-focus-visible/jump:opacity-100',
            )}
          />
        </button>

        {open && (
          <div className="absolute left-0 top-6 w-16 rounded-md bg-white py-1 shadow-lg ring-1 ring-slate-900/[0.06]">
            {years.map(item => {
              const isActive = item.year === activeYear;
              return (
                <button
                  type="button"
                  key={item.year}
                  onClick={() => {
                    onSelectYear(item);
                    setOpen(false);
                  }}
                  className={cn(
                    'block w-full px-2 py-1 text-left text-[11px] font-medium tabular-nums transition-colors',
                    isActive ? 'bg-gray-50 text-gray-800' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700',
                  )}
                >
                  {item.year}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

interface TimelineEventButtonProps {
  ev: ChronologyEventLight;
  isSelected: boolean;
  onClick: () => void;
  colorByCategory?: ReadonlyMap<string, string | null>;
  activeCategories?: ReadonlySet<string>;
  excludedTypes?: ReadonlySet<string>;
  isAggregate: boolean;
}

type TimelineTag = ChronologyEventLight['tags'][number];

function tagCategoryName(tag: TimelineTag): string {
  return getEventCategoryName(tag.group_label || tag.group_key || null);
}

function tagMatchesActiveFilters(
  tag: TimelineTag,
  activeCategories?: ReadonlySet<string>,
  excludedTypes?: ReadonlySet<string>,
): boolean {
  const categoryPasses = !activeCategories?.size || activeCategories.has(tagCategoryName(tag));
  return categoryPasses && !excludedTypes?.has(tag.tag_key);
}

function selectWinningGroupKey(
  tags: TimelineTag[],
  activeCategories?: ReadonlySet<string>,
  excludedTypes?: ReadonlySet<string>,
): string | null {
  if (!tags.length) return null;
  const hasTagFilters = Boolean(activeCategories?.size || excludedTypes?.size);
  const countedTags = hasTagFilters
    ? tags.filter(tag => tagMatchesActiveFilters(tag, activeCategories, excludedTypes))
    : tags;
  const candidates = countedTags.length ? countedTags : tags;
  const counts = new Map<string, number>();
  let winningKey: string | null = null;
  let winningCount = 0;

  for (const tag of candidates) {
    const key = tagCategoryName(tag);
    const count = (counts.get(key) ?? 0) + 1;
    counts.set(key, count);
    if (count > winningCount) {
      winningKey = key;
      winningCount = count;
    }
  }

  return winningKey;
}

/** The category colour an event is drawn with, favouring categories the user filtered to. */
export function getEventAccentStyle(
  ev: ChronologyEventLight,
  colorByCategory?: ReadonlyMap<string, string | null>,
  activeCategories?: ReadonlySet<string>,
  excludedTypes?: ReadonlySet<string>,
): GroupStyle {
  const winningGroupKey = selectWinningGroupKey(ev.tags || [], activeCategories, excludedTypes);
  const winningTag = ev.tags?.find(tag => tagCategoryName(tag) === winningGroupKey) ?? null;
  return getCategoryStyle(winningTag?.group_label || winningTag?.group_key, colorByCategory);
}

interface DisplayTagGroup {
  key: string;
  category: string | null | undefined;
  color: string | null | undefined;
  tags: TimelineTag[];
  isWinner: boolean;
}

function groupDisplayTags(tags: TimelineTag[], winningGroupKey: string | null): DisplayTagGroup[] {
  const groups = new Map<string, DisplayTagGroup>();

  tags.forEach(tag => {
    const category = tag.group_label || tag.group_key || null;
    const key = tagCategoryName(tag);
    const existing = groups.get(key);
    if (existing) {
      existing.tags.push(tag);
      return;
    }
    groups.set(key, {
      key,
      category,
      color: tag.group_color,
      tags: [tag],
      isWinner: key === winningGroupKey,
    });
  });

  return Array.from(groups.values())
    .sort((a, b) => Number(b.isWinner) - Number(a.isWinner));
}

function TimelineEventButton({
  ev,
  isSelected,
  onClick,
  colorByCategory,
  activeCategories,
  excludedTypes,
  isAggregate,
}: TimelineEventButtonProps) {
  const winningGroupKey = selectWinningGroupKey(ev.tags || [], activeCategories, excludedTypes);
  const tagLabel = ev.tags?.length ? ev.tags.map(tag => tag.label).join(' · ') : 'Untagged';
  const gStyle = getEventAccentStyle(ev, colorByCategory, activeCategories, excludedTypes);
  const displayTagGroups = groupDisplayTags(ev.tags || [], winningGroupKey);
  const background = isBackgroundEvent(ev);

  return (
    <button
      type="button"
      onMouseDown={event => event.preventDefault()}
      onClick={event => {
        if (event.detail > 0) event.currentTarget.blur();
        onClick();
      }}
      className={cn(
        'group relative w-full max-w-[52rem] rounded-lg py-2 pl-4 pr-3 text-left',
        'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-slate-300/70',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-y-0 right-0 left-2 rounded-lg transition-opacity duration-150',
          isSelected ? 'bg-gray-100 opacity-100' : 'opacity-0',
        )}
      />
      <div className="relative flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p
            className={cn(
              'line-clamp-2 text-sm leading-5 transition-colors duration-150',
              isSelected
                ? (background ? 'text-gray-700' : 'text-gray-950')
                : (background ? 'text-gray-500 group-hover:text-gray-700' : 'text-gray-800 group-hover:text-gray-950'),
            )}
          >
            <SearchHighlightedText text={ev.description} />
          </p>
          <div className="mt-1 flex min-w-0 items-center gap-1.5">
            {displayTagGroups.length ? (
              <p title={tagLabel} className="flex min-w-0 flex-wrap items-center gap-y-0.5 overflow-hidden text-[11px] leading-4">
                {displayTagGroups.flatMap(group => group.tags.map(tag => ({ tag, group }))).map(({ tag, group }, index) => {
                  const tagStyle = getTagStyle(group.color, group.category, colorByCategory);
                  return (
                    <span
                      key={tag.id}
                      className={cn(
                        'inline-flex max-w-full items-center font-medium',
                        group.isWinner ? tagStyle.text : 'text-gray-400',
                      )}
                    >
                      {index > 0 && <span className="mx-1.5 shrink-0 text-[13px] font-semibold leading-4 text-gray-400">·</span>}
                      <span className="min-w-0 truncate">{tag.label}</span>
                    </span>
                  );
                })}
              </p>
            ) : (
              <p title={tagLabel} className={cn('min-w-0 truncate text-[11px] font-medium leading-4', gStyle.text)}>
                {tagLabel}
              </p>
            )}
            {isAggregate && ev.chronology_name && (
              <span className="shrink-0 text-[10px] leading-4 text-gray-400">
                · {ev.chronology_name}
              </span>
            )}
            {background && (
              <span className="shrink-0 rounded-sm bg-slate-900/[0.05] px-1 text-[10px] font-medium leading-4 text-gray-500">
                Background
              </span>
            )}
          </div>
        </div>
        <div className="mt-[3px] flex shrink-0 items-center">
          <ChevronRight
            className={cn(
              'h-3.5 w-3.5 transition-[opacity,color] duration-150',
              isSelected ? 'text-gray-400 opacity-100' : 'text-gray-400 opacity-0 group-hover:opacity-100',
            )}
          />
        </div>
      </div>
    </button>
  );
}

function YearMarker({ year }: { year: string }) {
  return (
    <div data-year-anchor={year} className={cn('flex items-start', YEAR_ROW_CLASSNAME)}>
      <p className={cn(DATE_COLUMN_CLASSNAME, YEAR_LABEL_CLASSNAME)}>{year}</p>
    </div>
  );
}

interface DatedTimelineRowProps extends TimelineEventButtonProps {
  locale: RegionalDateLocale;
  yearMarker: string | null;
  showDate: boolean;
  /** The motion wrapper owns the anchor when a year label sits outside the collapsing row. */
  withAnchor?: boolean;
}

function DatedTimelineRow({
  ev,
  locale,
  yearMarker,
  showDate,
  withAnchor = true,
  isSelected,
  onClick,
  colorByCategory,
  activeCategories,
  excludedTypes,
  isAggregate,
}: DatedTimelineRowProps) {
  const currentYear = chronologyYearKey(ev.temporal_lower);
  const compactDate = showDate ? formatTemporalCompact(ev, currentYear ?? null, locale) : null;

  return (
    <div {...(withAnchor ? { 'data-event-id': ev.id } : {})}>
      {yearMarker && <YearMarker year={yearMarker} />}
      <div className="group/row flex items-start pb-3">
        <div className={cn(DATE_COLUMN_CLASSNAME, 'pt-2')}>
          {compactDate ? (
            <p className={cn('text-right text-xs leading-5 transition-colors', isSelected ? 'font-medium text-gray-900' : 'text-gray-500')}>
              {compactDate}
            </p>
          ) : null}
        </div>
        <EventNode ev={ev} isSelected={isSelected} colorByCategory={colorByCategory} activeCategories={activeCategories} excludedTypes={excludedTypes} />
        <div className="min-w-0 flex-1">
          <TimelineEventButton
            ev={ev}
            isSelected={isSelected}
            onClick={onClick}
            colorByCategory={colorByCategory}
            activeCategories={activeCategories}
            excludedTypes={excludedTypes}
            isAggregate={isAggregate}
          />
        </div>
      </div>
    </div>
  );
}

export interface ChronologyTimelineProps {
  datedEvents: ChronologyEventLight[];
  undatedEvents: ChronologyEventLight[];
  selectedEventId: string | null;
  onSelectEvent: (event: ChronologyEventLight) => void;
  colorByCategory?: ReadonlyMap<string, string | null>;
  activeCategories?: ReadonlySet<string>;
  excludedTypes?: ReadonlySet<string>;
  isAggregate: boolean;
  locale: RegionalDateLocale;
  showUndated: boolean;
  onToggleUndated: () => void;
  onShowUndated: () => void;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  /** Chronology identity. Changing it swaps the list in place instead of animating. */
  motionScope: string;
}

export function ChronologyTimeline({
  datedEvents,
  undatedEvents,
  selectedEventId,
  onSelectEvent,
  colorByCategory,
  activeCategories,
  excludedTypes,
  isAggregate,
  locale,
  showUndated,
  onToggleUndated,
  onShowUndated,
  scrollContainerRef,
  motionScope,
}: ChronologyTimelineProps) {
  const directSelectionRef = useRef<string | null>(null);
  const { pin: pinSelectedRow, clear: clearPinnedRow } = usePinnedRow(scrollContainerRef);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [activeYear, setActiveYear] = useState<string | null>(null);
  const datedRows = useMotionRows(datedEvents, motionScope);
  const undatedRows = useMotionRows(undatedEvents, motionScope);
  const datedAnimating = datedRows.some(row => row.leaving || row.entering);
  const datedListRef = useRef(datedEvents);
  const undatedListRef = useRef(undatedEvents);
  if (datedListRef.current !== datedEvents || undatedListRef.current !== undatedEvents) {
    datedListRef.current = datedEvents;
    undatedListRef.current = undatedEvents;
    // The click-to-open pin holds a row still by scrolling. Filter motion is the
    // row movement the user should see, so drop the pin as soon as the list changes.
    clearPinnedRow();
  }
  // Keep the list in normal flow for one frame after the animation so row heights
  // can be measured before the virtualizer takes back over.
  const [flowHold, setFlowHold] = useState(false);
  const rowHeightsRef = useRef(new Map<string, number>());
  const motionScopeRef = useRef(motionScope);
  const scopeChanged = motionScopeRef.current !== motionScope;
  if (scopeChanged) motionScopeRef.current = motionScope;
  if (scopeChanged) {
    if (flowHold) setFlowHold(false);
  } else if (datedAnimating && !flowHold) {
    setFlowHold(true);
  }
  const shouldVirtualize = !flowHold && !datedAnimating && datedEvents.length > TIMELINE_VIRTUALIZATION_THRESHOLD;
  const wasVirtualRef = useRef(shouldVirtualize);
  const committedScopeRef = useRef(motionScope);
  const pendingAnchorRef = useRef<{ id: string; top: number } | null>(null);
  if (wasVirtualRef.current !== shouldVirtualize) {
    const container = scrollContainerRef.current;
    pendingAnchorRef.current = container && committedScopeRef.current === motionScope
      ? visibleRowAnchor(container)
      : null;
    wasVirtualRef.current = shouldVirtualize;
  }
  const yearMarkers = useMemo(() => motionYearMarkers(datedRows), [datedRows]);
  const dateVisibility = useMemo(() => motionDateVisibility(datedRows, datedEvents), [datedRows, datedEvents]);
  const yearNavItems = useMemo(() => timelineYearNavItems(datedEvents), [datedEvents]);

  const datedVirtualizer = useVirtualizer({
    count: shouldVirtualize ? datedEvents.length : 0,
    getScrollElement: () => scrollContainerRef.current,
    // Measured sizes are cached by key and include the year marker, which depends on the
    // previous event, so the key must change when filtering adds or removes the marker.
    getItemKey: index => {
      const id = datedEvents[index]?.id;
      if (id === undefined) return index;
      return timelineVirtualRowKey(id, timelineRowHasYearMarker(datedEvents, index));
    },
    estimateSize: index => {
      const event = datedEvents[index];
      if (!event) return TIMELINE_EVENT_ROW_HEIGHT_PX;
      const key = timelineVirtualRowKey(event.id, timelineRowHasYearMarker(datedEvents, index));
      return rowHeightsRef.current.get(key) ?? timelineRowSize(datedEvents, index);
    },
    overscan: 6,
    useFlushSync: false,
  });

  const virtualItems = shouldVirtualize ? datedVirtualizer.getVirtualItems() : [];

  const scrollToYear = useCallback((item: TimelineYearNavItem) => {
    setActiveYear(item.year);

    if (shouldVirtualize) {
      datedVirtualizer.scrollToIndex(item.index, { align: 'start' });
      requestAnimationFrame(() => {
        scrollContainerRef.current?.scrollBy({ top: -36, behavior: 'auto' });
      });
      return;
    }

    scrollContainerRef.current
      ?.querySelector<HTMLElement>(`[data-year-anchor="${item.year}"]`)
      ?.scrollIntoView({ block: 'start', behavior: 'auto' });
  }, [datedVirtualizer, scrollContainerRef, shouldVirtualize]);

  useLayoutEffect(() => {
    if (datedAnimating || !flowHold) return;
    const container = scrollContainerRef.current;
    if (container) {
      for (const row of container.querySelectorAll<HTMLElement>('[data-event-id]')) {
        const id = row.dataset.eventId;
        if (!id || row.offsetHeight <= 0) continue;
        const hasYearMarker = row.querySelector('[data-year-anchor]') !== null;
        rowHeightsRef.current.set(timelineVirtualRowKey(id, hasYearMarker), row.offsetHeight);
      }
    }
    setFlowHold(false);
  }, [datedAnimating, flowHold, scrollContainerRef]);

  useLayoutEffect(() => {
    committedScopeRef.current = motionScope;
    const anchor = pendingAnchorRef.current;
    if (!anchor) return;
    pendingAnchorRef.current = null;
    const container = scrollContainerRef.current;
    if (!container) return;
    const row = eventRow(container, anchor.id);
    if (row) {
      const delta = row.getBoundingClientRect().top - anchor.top;
      if (Math.abs(delta) >= 1) container.scrollTop += delta;
      return;
    }
    const index = datedEvents.findIndex(event => event.id === anchor.id);
    if (index < 0) return;
    const offset = datedVirtualizer.getOffsetForIndex(index, 'start')?.[0];
    if (offset == null) return;
    container.scrollTop = offset - (anchor.top - container.getBoundingClientRect().top);
  });

  const handleSelectEvent = useCallback((ev: ChronologyEventLight) => {
    pinSelectedRow(ev.id);
    directSelectionRef.current = selectedEventId === ev.id ? null : ev.id;
    onSelectEvent(ev);
  }, [onSelectEvent, pinSelectedRow, selectedEventId]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const shouldShow = container.scrollTop > 300;
      setShowScrollTop(prev => (prev === shouldShow ? prev : shouldShow));

      let nextYear = yearNavItems[0]?.year ?? null;
      if (shouldVirtualize) {
        const firstVisible = datedVirtualizer.getVirtualItems()[0];
        nextYear = chronologyYearKey(datedEvents[firstVisible?.index]?.temporal_lower) ?? nextYear;
      } else {
        const containerTop = container.getBoundingClientRect().top;
        const anchors = container.querySelectorAll<HTMLElement>('[data-year-anchor]');
        anchors.forEach(anchor => {
          if (anchor.getBoundingClientRect().top <= containerTop + 96) {
            nextYear = anchor.dataset.yearAnchor ?? nextYear;
          }
        });
      }
      setActiveYear(prev => (prev === nextYear ? prev : nextYear));
    };

    handleScroll();
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [datedEvents, datedVirtualizer, scrollContainerRef, shouldVirtualize, yearNavItems]);

  useEffect(() => {
    if (!selectedEventId) {
      directSelectionRef.current = null;
      return;
    }
    if (directSelectionRef.current === selectedEventId) return;

    const container = scrollContainerRef.current;
    if (eventRowIsRendered(container, selectedEventId)) return;

    const datedIndex = datedEvents.findIndex(ev => ev.id === selectedEventId);
    if (datedIndex >= 0) {
      if (shouldVirtualize) {
        datedVirtualizer.scrollToIndex(datedIndex, { align: 'auto' });
        return;
      }

      requestAnimationFrame(() => {
        container
          ?.querySelector<HTMLElement>(`[data-event-id="${selectedEventId}"]`)
          ?.scrollIntoView({ block: 'nearest' });
      });
      return;
    }

    const undatedIndex = undatedEvents.findIndex(ev => ev.id === selectedEventId);
    if (undatedIndex < 0) return;
    if (!showUndated) {
      onShowUndated();
      return;
    }

    requestAnimationFrame(() => {
      container
        ?.querySelector<HTMLElement>(`[data-event-id="${selectedEventId}"]`)
        ?.scrollIntoView({ block: 'nearest' });
    });
  }, [
    selectedEventId,
    datedEvents,
    undatedEvents,
    shouldVirtualize,
    datedVirtualizer,
    showUndated,
    onShowUndated,
    scrollContainerRef,
  ]);

  return (
    <>
      {(datedEvents.length > 0 || datedAnimating) && (
        <div>
          <TimelineYearJump
            years={yearNavItems}
            activeYear={activeYear}
            onSelectYear={scrollToYear}
          />
          <div className="relative">
            <div className={cn(SPINE_LINE_CLASSNAME, 'top-[54px]')} />
            {shouldVirtualize ? (
              <div
                className="relative"
                style={{ height: `${datedVirtualizer.getTotalSize()}px` }}
              >
                {virtualItems.map(virtualItem => {
                  const ev = datedEvents[virtualItem.index];
                  if (!ev) return null;

                  return (
                    <div
                      key={ev.id}
                      ref={datedVirtualizer.measureElement}
                      data-index={virtualItem.index}
                      className="absolute left-0 top-0 w-full"
                      style={{ transform: `translateY(${virtualItem.start}px)` }}
                    >
                      <DatedTimelineRow
                        ev={ev}
                        locale={locale}
                        yearMarker={timelineRowHasYearMarker(datedEvents, virtualItem.index) ? chronologyYearKey(ev.temporal_lower) : null}
                        showDate={eventShowsDate(datedEvents, virtualItem.index)}
                        isSelected={selectedEventId === ev.id}
                        onClick={() => handleSelectEvent(ev)}
                        colorByCategory={colorByCategory}
                        activeCategories={activeCategories}
                        excludedTypes={excludedTypes}
                        isAggregate={isAggregate}
                      />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div>
                {datedRows.map(row => {
                  const year = chronologyYearKey(row.event.temporal_lower);
                  const yearPlace = yearMarkers.get(row.event.id);
                  return (
                    <div key={row.event.id} data-event-id={row.event.id}>
                      {yearPlace === 'outside' && year && <YearMarker year={year} />}
                      <MotionRowFrame row={row}>
                        <DatedTimelineRow
                          ev={row.event}
                          locale={locale}
                          yearMarker={yearPlace === 'inside' ? year : null}
                          showDate={dateVisibility.get(row.event.id) ?? false}
                          withAnchor={false}
                          isSelected={selectedEventId === row.event.id}
                          onClick={() => handleSelectEvent(row.event)}
                          colorByCategory={colorByCategory}
                          activeCategories={activeCategories}
                          excludedTypes={excludedTypes}
                          isAggregate={isAggregate}
                        />
                      </MotionRowFrame>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {undatedRows.length > 0 && (
        <div className={cn((datedEvents.length > 0 || datedAnimating) && 'mt-3')}>
          <button
            type="button"
            onClick={onToggleUndated}
            aria-expanded={showUndated}
            className={cn('group flex w-full items-start text-left', YEAR_ROW_CLASSNAME)}
          >
            <span className={cn(DATE_COLUMN_CLASSNAME, YEAR_LABEL_CLASSNAME)}>Undated</span>
            <span className="ml-[35px] flex items-center gap-0.5 text-xs leading-5 text-gray-500 transition-colors group-hover:text-gray-800">
              {undatedEvents.length} {undatedEvents.length === 1 ? 'event' : 'events'}
              <ChevronDown className={cn('h-3 w-3 text-gray-400 transition-transform duration-150', showUndated && 'rotate-180')} />
            </span>
          </button>
          {showUndated && (
            <div className="relative">
              <div className={cn(SPINE_LINE_CLASSNAME, 'top-[18px]')} />
              {undatedRows.map(row => (
                <MotionRowFrame key={row.event.id} row={row}>
                  <div data-event-id={row.event.id} className="group/row flex items-start pb-3">
                    <div className={DATE_COLUMN_CLASSNAME} />
                    <EventNode
                      ev={row.event}
                      isSelected={selectedEventId === row.event.id}
                      colorByCategory={colorByCategory}
                      activeCategories={activeCategories}
                      excludedTypes={excludedTypes}
                    />
                    <div className="min-w-0 flex-1">
                      <TimelineEventButton
                        ev={row.event}
                        isSelected={selectedEventId === row.event.id}
                        onClick={() => handleSelectEvent(row.event)}
                        colorByCategory={colorByCategory}
                        activeCategories={activeCategories}
                        excludedTypes={excludedTypes}
                        isAggregate={isAggregate}
                      />
                    </div>
                  </div>
                </MotionRowFrame>
              ))}
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
        className={cn(
          'sticky bottom-4 left-full -translate-x-10 h-8 w-8 rounded-full border border-gray-200 bg-white shadow-xs',
          'flex items-center justify-center text-gray-400 hover:border-gray-300 hover:text-gray-600 hover:shadow-sm',
          'transition-all duration-200',
          showScrollTop ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
        )}
        aria-label="Scroll to top"
      >
        <ArrowUp className="h-4 w-4" />
      </button>
    </>
  );
}
